import HelpOutline from '@mui/icons-material/HelpOutline';
import LaunchOutlinedIcon from '@mui/icons-material/LaunchOutlined';
import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Stack
} from '@mui/material';
import {
  FormatCurrency,
  FormatDate,
  FormatPercent,
  IconTooltip,
  Text
} from '@vestwell-frontend/ui';
import {
  useWentworthBalanceGetCurrentBalance,
  useWentworthBalanceGetLoanBalance,
  useWentworthBalanceGetRateOfReturn,
  useWentworthBalanceGetVestedBalance
} from '@vestwell-unified-api/hooks';

import { format, startOfYear } from 'date-fns';
import { FC } from 'react';

import { RateOfReturnDrawer } from './RateOfReturn/RateOfReturnDrawer.component';

export type CurrentBalanceProps = {
  employeeId: number;
};

export const CurrentBalance: FC<CurrentBalanceProps> = props => {
  const currentBalance = useWentworthBalanceGetCurrentBalance(
    {
      asOfDate: format(new Date(), 'MM-dd-yyyy'),
      participantId: props.employeeId
    },
    {
      query: {
        enabled: !!props.employeeId
      }
    }
  );

  const loanBalance = useWentworthBalanceGetLoanBalance(
    {
      participantId: props.employeeId
    },
    {
      query: {
        enabled: !!props.employeeId
      }
    }
  );

  const vestedBalance = useWentworthBalanceGetVestedBalance(
    {
      dateOf: format(new Date(), 'MM-dd-yyyy'),
      participantId: props.employeeId
    },
    {
      query: {
        enabled: !!props.employeeId
      }
    }
  );

  const rateOfReturn = useWentworthBalanceGetRateOfReturn(
    {
      endDate: format(new Date(), 'MM-dd-yyyy'),
      participantId: props.employeeId,
      startDate: format(startOfYear(new Date()), 'MM-dd-yyyy')
    },
    {
      query: {
        enabled: !!props.employeeId
      }
    }
  );

  return (
    <div data-testid='participant-contributions-card'>
      <Card elevation={0} variant='outlined'>
        <CardContent>
          <Text data-testid='current-balance-header' mb={0.5} variant='h5'>
            Current Balance
          </Text>
          <Text
            color='grey'
            data-testid='ass-of-date'
            display='inline-block'
            mb={1.5}
            variant='caption'>
            As of <FormatDate format='MM/dd/yyyy' value={new Date()} />
          </Text>
          <Text isLoading={currentBalance.isLoading} mb={0.5} variant='h4'>
            <FormatCurrency
              data-testid='current-balance-amount'
              precision={2}
              stripTrailingZeros={false}
              value={Number(currentBalance.data?.totalBalance || 0)}
            />
          </Text>
          {!!loanBalance.data?.outstandingLoanBalance &&
            Number(loanBalance.data?.outstandingLoanBalance) > 0 && (
              <Text
                color='grey'
                display='inline-block'
                mb={0.5}
                variant='caption'>
                Excludes loan balances
              </Text>
            )}

          <Box mb={1.5} mt={1}>
            <Divider orientation='horizontal' />
          </Box>

          <Text
            color='grey'
            data-testid='personal-rate-of-return-header'
            mb={0.5}>
            Personal Rate of Return
          </Text>
          <Stack alignItems='center' direction='row' mb={-0.5} spacing={1}>
            {![undefined, null].includes(
              rateOfReturn?.data?.customDateRangeRateOfReturn
            ) ? (
              <Text
                color='success'
                isLoading={rateOfReturn.isLoading}
                variant='h5'>
                <FormatPercent
                  data-testid='personal-rate-of-return-amount'
                  value={rateOfReturn.data?.customDateRangeRateOfReturn}
                />
              </Text>
            ) : (
              <>
                <Text>Not available</Text>
                <IconTooltip
                  color='primary'
                  placement='top'
                  trigger={<HelpOutline color='primary' fontSize='small' />}>
                  Your YTD Rate of Return (RoR) is unavailable, likely due to
                  limited investment activity or the new year. Click 'Change
                  Range' to view or customize past performance.
                </IconTooltip>
              </>
            )}
          </Stack>
          <Stack alignItems='center' direction='row' justifyContent='start'>
            <Text color='grey' m={0} variant='caption'>
              Year to Date
            </Text>
            <Text mb={0} ml={1}>
              |
            </Text>
            <RateOfReturnDrawer
              data-testid='rate-of-return-drawer'
              employeeId={props.employeeId}
            />
          </Stack>
          <Box mb={1.5} mt={0.5}>
            <Divider orientation='horizontal' />
          </Box>
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'
            mb={1.5}
            spacing={4}>
            <Text color='grey'>
              Vested Amount{' '}
              <IconButton
                color='primary'
                onClick={() =>
                  window.open(`/participants/${props.employeeId}/vesting`)
                }
                size='small'>
                <LaunchOutlinedIcon fontSize='inherit' />
              </IconButton>
            </Text>
            <Text isLoading={vestedBalance.isLoading} mb={0}>
              <FormatCurrency
                data-testid='vested-amount'
                precision={2}
                stripTrailingZeros={false}
                value={Number(vestedBalance.data?.vestedBalance || 0)}
              />
            </Text>
          </Stack>
          <Stack
            alignItems='center'
            direction='row'
            justifyContent='space-between'
            spacing={4}>
            <Text color='grey' mb={2} mt={2}>
              Unvested Amount{' '}
              <IconButton
                color='primary'
                onClick={() =>
                  window.open(`/participants/${props.employeeId}/vesting`)
                }
                size='small'>
                <LaunchOutlinedIcon fontSize='inherit' />
              </IconButton>
            </Text>

            <Text isLoading={vestedBalance.isLoading}>
              <FormatCurrency
                data-testid='vested-amount'
                precision={2}
                stripTrailingZeros={false}
                value={Number(vestedBalance.data?.unvestedBalance || 0)}
              />
            </Text>
          </Stack>
          {!!loanBalance.data?.outstandingLoanBalance && (
            <Box mb={1.5} mt={1.5}>
              <Divider orientation='horizontal' />
            </Box>
          )}

          {!!loanBalance.data?.outstandingLoanBalance && (
            <Stack
              alignItems='center'
              direction='row'
              justifyContent='space-between'
              mb={0.5}
              spacing={4}>
              <Text color='grey'>
                Loan Balance{' '}
                <IconButton
                  color='primary'
                  onClick={() =>
                    window.open(`/participants/${props.employeeId}/loans`)
                  }
                  size='small'>
                  <LaunchOutlinedIcon fontSize='inherit' />
                </IconButton>
              </Text>
              <Text isLoading={loanBalance.isLoading}>
                <FormatCurrency
                  data-testid='vested-amount'
                  precision={2}
                  stripTrailingZeros={false}
                  value={Number(loanBalance.data?.outstandingLoanBalance || 0)}
                />
              </Text>
            </Stack>
          )}
          <Box pb={1.5} />
        </CardContent>
      </Card>
    </div>
  );
};
